/**
 * Public account-deletion page (DPDP P-16) at /legal/data-deletion — the deletion URL for the
 * app-store listings, and the path the mobile app's Legal screen points to.
 *
 * Any account holder (patient, doctor, pharmacist, staff) can use it without signing in or
 * installing the app: verify the registered mobile number with a one-time code, give a reason,
 * and the request joins the same admin review queue as requests raised inside the app.
 *
 *   phone → code + reason → "delete" typed to confirm → done (a new request, or the one already
 *   in progress)
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  confirmAccountDeletion,
  requestDeletionOtp,
  type AccountDeletion as DeletionResult,
} from "../../api/auth";
import { ApiError, describeError } from "../../api/errors";
import { Logo } from "../../components/Logo";
import { ConfirmDelete } from "../../components/ui/ConfirmDelete";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { Select } from "../../components/ui/Select";
import { isValidIndianPhone } from "../../lib/phone";

type Step = "phone" | "verify" | "done";

// Same choices as the mobile app's deletion form.
const REASONS = ["Privacy concerns", "Not using it", "Found an alternative", "Other"];

const CHANNEL_COPY: Record<string, string> = {
  whatsapp: " on WhatsApp",
  sms: " by SMS",
};

const STATUS: Record<DeletionResult["status"], { label: string; badge: string }> = {
  pending: { label: "Awaiting review", badge: "badge-warning" },
  approved: { label: "Approved — deletion in progress", badge: "badge-info" },
};

const CODE_MESSAGES: Record<string, string> = {
  RATE_LIMIT_EXCEEDED: "Too many attempts. Please try again later.",
  INVALID_OTP: "That code is incorrect. Please try again.",
  OTP_EXPIRED: "This code has expired. Request a new one.",
  OTP_ATTEMPTS_EXCEEDED: "Too many incorrect attempts. Request a new code.",
};

function messageFor(err: unknown): string {
  if (err instanceof ApiError && CODE_MESSAGES[err.code]) return CODE_MESSAGES[err.code];
  return describeError(err);
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

export function AccountDeletion() {
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("+91");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [channel, setChannel] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState("");
  const [result, setResult] = useState<DeletionResult | null>(null);
  const [busy, setBusy] = useState(false);
  // The request is sent only after "delete" is typed in the confirmation.
  const [confirming, setConfirming] = useState(false);

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    if (!isValidIndianPhone(phone)) {
      toast.error("Enter a 10-digit mobile number starting with 6, 7, 8, or 9.");
      return;
    }
    setBusy(true);
    try {
      const res = await requestDeletionOtp(phone.trim());
      setRequestId(res.request_id);
      setChannel(res.channel);
      setCode("");
      setStep("verify");
      if (res.dev_otp) {
        toast.info(`Your code is ${res.dev_otp}`, {
          description: "Test server — the code is shown here as well.",
          duration: 10000,
        });
      }
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  function askToConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (requestId) setConfirming(true);
  }

  async function submit() {
    if (!requestId) return;
    setBusy(true);
    try {
      const note = details.trim();
      const fullReason = note ? `${reason}: ${note}` : reason;
      setResult(await confirmAccountDeletion(requestId, code.trim(), fullReason));
      setStep("done");
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <main className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-sunken px-4 py-10">
      <div className="brand-gradient absolute inset-x-0 top-0 h-1.5" aria-hidden />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-linear-to-b from-brand-50 via-brand-50/40 to-transparent"
        aria-hidden
      />

      <div className="relative w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Logo variant="stacked" size={58} withTagline to="/" />
        </div>

        <div className="card overflow-hidden">
          <div className="brand-gradient h-1" aria-hidden />
          <div className="p-6">
            <h1 className="mb-1 text-xl font-bold text-brand-900">
              {step !== "done" || !result
                ? "Delete your account"
                : result.already_requested
                  ? "Already requested"
                  : "Request received"}
            </h1>

            {step === "phone" && (
              <>
                <p className="mb-5 text-sm text-ink-muted">
                  Request deletion of your EMEDIEXP account and the personal data linked to it.
                </p>
                <ol className="panel mb-5 list-decimal space-y-2 pl-9 text-sm text-ink-soft">
                  <li>Verify the mobile number registered with your account.</li>
                  <li>Our team reviews the request and lets you know the outcome.</li>
                  <li>
                    Once approved, your personal details are erased and the account is closed.
                    Records the law requires us to keep, such as prescriptions, are retained
                    only for the required period.
                  </li>
                </ol>
                <form onSubmit={sendCode} className="space-y-4">
                  <PhoneInput
                    label="Registered mobile number"
                    value={phone}
                    onChange={setPhone}
                    required
                    autoFocus
                  />
                  <button type="submit" disabled={busy} className="btn-primary btn-block">
                    {busy ? "Sending…" : "Send verification code"}
                  </button>
                </form>
                <p className="mt-4 text-center text-xs text-ink-muted">
                  You can also do this in the EMEDIEXP app under Settings → Delete Account.
                </p>
              </>
            )}

            {step === "verify" && (
              <form onSubmit={askToConfirm} className="space-y-4">
                <p className="text-sm text-ink-muted">
                  Enter the 6-digit code sent{CHANNEL_COPY[channel ?? ""] ?? ""} to{" "}
                  <span className="font-medium text-ink">{phone}</span>.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  minLength={6}
                  maxLength={6}
                  className="input text-center text-lg tracking-[0.4em]"
                  aria-label="Verification code"
                  required
                  autoFocus
                />
                <label className="block">
                  <span className="label">Why are you leaving?</span>
                  <Select size="md" value={reason} options={REASONS.map((r) => ({ value: r, label: r }))} onChange={setReason} />
                </label>
                <label className="block">
                  <span className="label">Anything else? (optional)</span>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    maxLength={400}
                    rows={3}
                    className="textarea"
                  />
                </label>
                <p className="rounded-xl bg-danger-soft px-3.5 py-2.5 text-xs text-danger">
                  Once the request is approved, the account can’t be restored.
                </p>
                <button type="submit" disabled={busy} className="btn-danger btn-block">
                  {busy ? "Submitting…" : "Request account deletion"}
                </button>
                <div className="flex justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => setStep("phone")}
                    className="text-ink-muted transition hover:text-brand"
                  >
                    Change number
                  </button>
                  <button
                    type="button"
                    onClick={() => sendCode()}
                    disabled={busy}
                    className="link disabled:opacity-50"
                  >
                    Resend code
                  </button>
                </div>
              </form>
            )}

            {step === "done" && result && (
              <div className="space-y-4">
                <p className="text-sm text-ink-soft">
                  {result.already_requested
                    ? "This account already has a deletion request in progress, so we haven’t opened another one."
                    : `Our team reviews deletion requests within ${result.review_within_days} days, and you’ll be notified of the outcome.`}
                </p>
                <div className="panel flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="whitespace-nowrap text-ink-muted">
                    Submitted{" "}
                    <span className="font-medium text-ink">{formatDate(result.created_at)}</span>
                  </span>
                  <span
                    className={`${STATUS[result.status]?.badge ?? "badge-neutral"} whitespace-nowrap`}
                  >
                    {STATUS[result.status]?.label ?? result.status}
                  </span>
                </div>
                <Link to="/" className="btn-secondary btn-block">
                  Back to EMEDIEXP
                </Link>
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-ink-muted">Express care. Always there.</p>
      </div>

      <ConfirmDelete
        open={confirming}
        title="Delete your account?"
        confirmLabel="Request account deletion"
        busyLabel="Submitting…"
        busy={busy}
        onConfirm={() => void submit()}
        onClose={() => setConfirming(false)}
      >
        <p>
          Our team reviews the request. Once it is approved, your personal details are erased and the account is
          closed; it can’t be restored.
        </p>
      </ConfirmDelete>
    </main>
  );
}
